// add routes here so they update everywhere when changed.
import {
  getStudioLanguageFromPathname,
  stripStudioLocalePrefixFromPathname,
} from '@repo/react-router-locale/canonicalize-studio-locale-pathname'
import type { FontGeneratorFontCreateInput } from 'codegen/types'
import { isProduction } from 'utils/env'
import { ROUTES as FLOW_ROUTES } from './routes/flow/routes'
import { getNewFontByInputUrl } from './routes/font-generator/utils/route'

const rootPath = isProduction
  ? 'https://www.creativefabrica.com'
  : 'https://staging.creativefabrica.com'

// Opens that template's modal on load for the ai-video-generator templates routes.
export const TEMPLATE_QUERY_PARAM = 'template'
export const DRAFT_UPSCALE_QUERY_PARAMS = { SESSION_ID: 'draft', MEDIA_ID: 'draftMedia' } as const

// React Router matches these by string, so the route definition and every `useRouteLoaderData`
// reader have to agree on them.
export const ROUTE_IDS = {
  AI_IMAGE_EDITOR_HOME: 'ai-image-editor-home',
  AI_VIDEO_EDITOR_HOME: 'ai-video-editor-home',
  FREEMODE_AI_IMAGE_EDITOR_HOME: 'freemode-ai-image-editor-home',
  FREEMODE_AI_VIDEO_EDITOR_HOME: 'freemode-ai-video-editor-home',
} as const

function assetAppRoutes(root: string, yourAssetsPath: string) {
  return {
    ROOT: root,
    HOME: root,
    YOUR_ASSETS: `${root}/${yourAssetsPath}`,
    PRESETS: `${root}/presets`,
    COMMUNITY: `${root}/community`,
    FAVORITES: `${root}/favorites`,
    LIGHTBOX: (id?: string) => (id ? `${root}/lightbox/${id}` : `${root}/lightbox/:assetId`),
  }
}

// English paths. Translated address-bar slugs live in studio-route-slugs locale JSON.
export const ROUTES = {
  ACCOUNT_SETTINGS: {
    ROOT: '/settings',
    MY_ACCOUNT: '/settings/my-account',
    PROFILE_SETTINGS: '/settings/my-account/settings',
    CONTENT_MODERATION: '/settings/content-moderation',
    SOCIAL_MEDIA: '/settings/social-media',
    BILLING_DETAILS: '/settings/billing-details',
    SOCIAL_LOGIN_ACCOUNTS: '/settings/social-accounts',
    PAYMENT_METHODS: '/settings/payment-methods',
    NOTIFICATIONS: '/settings/notifications',
    SHIPPING_ADDRESS: '/settings/shipping-address',
    LOGIN_DETAILS: '/settings/login-details',
    LOGOUT: '/settings/logout',
    ACCOUNT_ACTIVITY: '/settings/account-activity',
    DELETE_ACCOUNT: '/settings/delete-account',
    SUBSCRIPTIONS: '/settings/subscriptions',
    MANAGE_SUBSCRIPTION: (subscriptionId?: string) =>
      subscriptionId
        ? `/settings/manage-subscription/${subscriptionId}`
        : '/settings/manage-subscription/:subscriptionId',
    ORDERS: '/settings/orders',
  },
  STUDIO: {
    ROOT: '/',
    HOME: '/',
    STUDIO: '/studio',
    ALL_TOOLS: '/studio/all-tools',
    RESET_PASSWORD: '/reset-password',
    DEFAULT_LOGGED_OUT: '/studio/create',
    UNAUTHORIZED_TO_VIEW_PROJECT: '/?unauthorized=true',
    NEW_PROJECT: '/studio/create',
    NEW_PROJECT_WITH_TEMPLATE: (templateCategory: string, templateId?: string) =>
      // oxlint-disable-next-line no-negated-condition
      templateId !== undefined
        ? `/studio/create/?template_category=${templateCategory}&content_template_id=${templateId}`
        : `/studio/create/`,
    EDITOR: {
      ROOT: `/studio/create`,
      PROJECT: (id?: string) => (id ? `/studio/create/${id}` : `/studio/create/:id`),
      NEW_PROJECT_WITH_TEMPLATE: (templateId?: string) =>
        // oxlint-disable-next-line no-negated-condition
        templateId !== undefined
          ? `/studio/create/?template_category=template&content_template_id=${templateId}`
          : `/studio/create/`,
    },
    // can be used for matchPath
    PROJECT: (id?: string) => (id ? `/studio/create/${id}` : '/studio/create/:id'),
    PROJECTS: '/studio/projects',
    FOLDERS: {
      FOLDER: (folderId?: string) =>
        folderId ? `/studio/folders/${folderId}` : '/studio/folders/:folderId',
    },
    TRASH: '/studio/trash',
    SETTINGS: {
      ROOT: '/studio/settings',
      LOCAL_MODELS: '/studio/settings?tab=local-models',
      SYSTEM: '/studio/settings?tab=system',
      AURORA: '/studio/settings/aurora',
    },
  },
  FONT_GENERATOR: {
    ROOT: '/font-generator',
    EDIT_FONT: (id?: string) => (id ? `/font-generator/${id}` : '/font-generator/:id'),
    FONT_LIGHTBOX: (id?: string) =>
      id ? `/font-generator/lightbox/${id}` : '/font-generator/lightbox/:id',
    NEWEST_FONTS: '/font-generator/newest',
    CLONE_FONT: (fontInfo?: FontGeneratorFontCreateInput | null) => getNewFontByInputUrl(fontInfo),
  },
  FLOW: FLOW_ROUTES,
  CREATOR: {
    ROOT: '/creator',
    CREATOR: (userSlug?: string) => (userSlug ? `/creator/${userSlug}` : '/creator/:userSlug'),
    CREATOR_IMAGES: (userSlug?: string) =>
      userSlug ? `/creator/${userSlug}/images` : '/creator/:userSlug/images',
    CREATOR_MUSIC: (userSlug?: string) =>
      userSlug ? `/creator/${userSlug}/music` : '/creator/:userSlug/music',
    CREATOR_VIDEOS: (userSlug?: string) =>
      userSlug ? `/creator/${userSlug}/videos` : '/creator/:userSlug/videos',
    CREATOR_3D: (userSlug?: string) =>
      userSlug ? `/creator/${userSlug}/3d` : '/creator/:userSlug/3d',
    CREATOR_SVG: (userSlug?: string) =>
      userSlug ? `/creator/${userSlug}/svg` : '/creator/:userSlug/svg',
  },
  DESIGN_REVIEWER: {
    ROOT: '/ai-design-reviewer',
    DESIGN: (id?: string) => (id ? `/ai-design-reviewer/${id}` : '/ai-design-reviewer/:id'),
  },
  AI_IMAGE_EDITOR: {
    ROOT: '/ai-image-editor',
    SESSION: (id?: string) => (id ? `/ai-image-editor/${id}` : '/ai-image-editor/:sessionId'),
    SESSION_BY_IMAGE: (image?: string) =>
      image ? `/ai-image-editor?image=${encodeURIComponent(image)}` : '/ai-image-editor',
    SESSION_BY_IMAGE_AND_PROMPT: (image: string, prompt: string) =>
      `/ai-image-editor?image=${encodeURIComponent(image)}&prompt=${encodeURIComponent(prompt)}`,
  },
  QUOTE_GENERATOR: {
    ROOT: '/ai-quote-generator',
    SESSION: (id?: string) => (id ? `/ai-quote-generator/${id}` : '/ai-quote-generator/:sessionId'),
    LIGHTBOX: (sessionId: string, imageId: string) =>
      `/ai-quote-generator/lightbox/${sessionId}/${imageId}`,
  },
  AI_COLLAGE_MAKER: {
    ROOT: '/ai-collage-maker',
  },
  VECTORIZER: {
    ROOT: '/vectorizer',
    IMAGE: (id?: string) => (id ? `/vectorizer/${id}` : '/vectorizer/:id'),
    SESSION_BY_IMAGE: (image?: string) =>
      image ? `/vectorizer?image=${encodeURIComponent(image)}` : '/vectorizer',
  },
  BACKGROUND_REMOVER: {
    ROOT: '/background-remover',
    IMAGE: (id?: string) => (id ? `/background-remover/${id}` : '/background-remover/:id'),
    SESSION_BY_IMAGE: (image?: string) =>
      image ? `/background-remover?image=${encodeURIComponent(image)}` : '/background-remover',
  },
  LOGO_CREATOR: {
    ROOT: '/logo-creator',
    ID: (id?: string) => (id ? `/logo-creator/${id}` : '/logo-creator/:id'),
    LIGHTBOX: (id?: string) => (id ? `/logo-creator/lightbox/${id}` : '/logo-creator/lightbox/:id'),
  },
  UPSCALER: {
    ROOT: '/image-upscaler',
    IMAGE: (id?: string) => (id ? `/image-upscaler/${id}` : '/image-upscaler/:id'),
  },
  IMAGE_OUTPAINTING: {
    ROOT: '/image-outpainting',
  },
  IMAGE_LAYERIZER: {
    ROOT: '/image-layerizer',
    IMAGE: (id?: string) => (id ? `/image-layerizer/${id}` : '/image-layerizer/:id'),
  },
  MUSIC_EDITOR: {
    ROOT: '/music-editor',
  },
  IMAGE_INPAINTING: {
    ROOT: '/image-inpainting',
  },
  AI_WATERMARK_REMOVER: {
    ROOT: '/ai-watermark-remover',
    IMAGE: (id?: string) => (id ? `/ai-watermark-remover/${id}` : '/ai-watermark-remover/:id'),
  },
  REMOVE_PEOPLE_FROM_BACKGROUND: {
    ROOT: '/remove-people-from-background',
    IMAGE: (id?: string) =>
      id ? `/remove-people-from-background/${id}` : '/remove-people-from-background/:id',
  },
  DOODLE_PET_GENERATOR: {
    ROOT: '/doodle-pet-generator',
    IMAGE: (id?: string) => (id ? `/doodle-pet-generator/${id}` : '/doodle-pet-generator/:id'),
  },
  WEDDING_INVITE_GENERATOR: {
    ROOT: '/ai-wedding-invite-generator',
    IMAGE: (id?: string) =>
      id ? `/ai-wedding-invite-generator/${id}` : '/ai-wedding-invite-generator/:id',
  },
  AI_COLORING_PAGE_MAKER: {
    ROOT: '/ai-coloring-page-maker',
    IMAGE: (id?: string) => (id ? `/ai-coloring-page-maker/${id}` : '/ai-coloring-page-maker/:id'),
  },
  TOON_FAMILY_BUILDER: {
    ROOT: '/toon-family-builder',
  },
  BOARDS: {
    ROOT: '/boards',
  },
  HEALTH_CHECK: {
    ROOT: '/healthcheck',
  },
  TRANSFER: {
    ROOT: '/transfer',
  },
  FILE_CONVERTER: {
    ROOT: '/file-converter',
  },
  IMAGE_COMPRESSOR: {
    ROOT: '/image-compressor',
  },
  IMAGE_RESIZER: {
    ROOT: '/image-resizer',
  },
  COLOR_PALETTE_PICKER: {
    ROOT: '/color-palette-picker',
  },
  ETSY_COACH: {
    ROOT: '/etsy-coach',
    SESSION: (id?: string) => (id ? `/etsy-coach/${id}` : '/etsy-coach/:sessionId'),
  },
  SHARE: {
    ROOT: '/share',
    ID: (id?: string) => (id ? `/share/${id}` : '/share/:id'),
  },
  TEMPLATES: {
    ROOT: '/templates',
    FAVORITES: '/templates/favorites',
    ITEM: {
      ROOT: '/templates/item',
      ID: (id?: string) => (id ? `/templates/item/${id}` : '/templates/item/:id'),
    },
    GALLERY: '/templates/:category?/:subcategory?',
  },
  COINS: {
    ROOT: '/coins',
    TRANSACTIONS: '/coins/transactions',
    TOPUPS: '/coins/topups',
  },
  CONTENT_MODERATION: {
    ROOT: '/content-moderation',
  },
  MCP: {
    ROOT: '/mcp',
  },
  PRICING: {
    ROOT: '/pricing',
  },
  REVIEWS: {
    ROOT: '/reviews',
  },
  TEAM: {
    ROOT: '/team',
  },
  REFERRALS: {
    ROOT: '/referrals',
  },
  AFFILIATES: {
    ROOT: '/affiliates',
  },
  EXTERNAL: {
    CREATIVE_FABRICA: {
      COINS: `${rootPath}/coins/`,
      EDIT_PROFILE: `${rootPath}/my-account/edit-account/`,
      MY_PROFILE: `${rootPath}/my-account/my-profile/`,
      SUBSCRIPTION: `${rootPath}/subscription/`,
      ACCOUNT_SUBSCRIPTIONS: `${rootPath}/my-account/subscriptions/`,
      ACCOUNT_PAYMENT_METHODS: `${rootPath}/my-account/payment-methods/`,
      ACCOUNT_ORDERS: `${rootPath}/my-account/orders/`,
      MY_ACCOUNT: `${rootPath}/my-account/`,
      ACCOUNT_ACTIVITY: `${rootPath}/my-account/activity/`,
      DELETE_ACCOUNT: `${rootPath}/my-account/delete-account/`,
      AFFILIATE_AREA: {
        ROOT: `${rootPath}/affiliate-area/`,
        URLS: `${rootPath}/affiliate-area/?tab=urls`,
        STATS: `${rootPath}/affiliate-area/?tab=stats`,
        REFERRALS: `${rootPath}/affiliate-area/?tab=referrals`,
        VISITS: `${rootPath}/affiliate-area/?tab=visits`,
        SETTINGS: `${rootPath}/affiliate-area/?tab=settings`,
      },
    },
  },
  ALL_COMMENTS: '/all-comments',
  AIDA: '/_aida',
  COMPONENTS: '/_components',
  DESIGN: '/_design',
  FEATURES: '/_features',
  ROUTE_STORIES: '/_routes',
  RENDER: '/_render',
  AI_MUSIC_GENERATOR: {
    ROOT: '/ai-music-generator',
    MY_MUSIC: '/ai-music-generator/my-music',
    TRENDING: '/ai-music-generator/trending',
    ARTIST: (userSlug?: string) =>
      userSlug ? `/ai-music-generator/artist/${userSlug}` : '/ai-music-generator/artist/:userSlug',
    SESSION: (id?: string) => (id ? `/ai-music-generator/${id}` : '/ai-music-generator/:sessionId'),
  },
  AI_VIDEO_EDITOR: {
    ROOT: '/ai-video-editor',
    SESSION: (id?: string) => (id ? `/ai-video-editor/${id}` : '/ai-video-editor/:sessionId'),
    WITH_VIDEO: (video: string) => `/ai-video-editor?${new URLSearchParams({ video }).toString()}`,
    SESSION_BY_VIDEO_AND_PROMPT: (video: string, prompt: string) =>
      `/ai-video-editor?video=${encodeURIComponent(video)}&prompt=${encodeURIComponent(prompt)}`,
  },
  AI_VIDEO_GENERATOR: {
    ROOT: '/ai-video-generator',
    WITH_PROMPT: (prompt: string) =>
      `/ai-video-generator?${new URLSearchParams({ prompt }).toString()}`,
    COMMUNITY: '/ai-video-generator',
    MY_VIDEOS: '/ai-video-generator/my-videos',
    FAVORITES: '/ai-video-generator/favorites',
    TEMPLATES: '/ai-video-generator/templates',
    MOTION: '/ai-video-generator/motion',
    TEMPLATES_MARKETING: '/ai-video-generator/templates/marketing',
    TEMPLATES_MARKETING_SUBCATEGORY: (sub?: string) =>
      sub
        ? `/ai-video-generator/templates/marketing/${sub}`
        : '/ai-video-generator/templates/marketing/:subcategory',
    SESSION: (id?: string) => (id ? `/ai-video-generator/${id}` : '/ai-video-generator/:sessionId'),
    SESSION_MEDIA: (sessionId: string, mediaId: string) =>
      `/ai-video-generator/${sessionId}/${mediaId}`,
    WITH_START_FRAME: ({
      prompt,
      startFrameUrl,
      model,
    }: {
      prompt: string
      startFrameUrl: string
      model: string
    }) => `/ai-video-generator?${new URLSearchParams({ prompt, startFrameUrl, model }).toString()}`,
  },
  // Private Freemode video tool — own base path so its links stay in-tool.
  AI_VIDEO_UNCENSORED: {
    ROOT: '/ai-video-uncensored',
    MY_VIDEOS: '/ai-video-uncensored/my-videos',
    TEMPLATES: '/ai-video-uncensored/templates',
    SESSION: (id?: string) =>
      id ? `/ai-video-uncensored/${id}` : '/ai-video-uncensored/:sessionId',
    SESSION_MEDIA: (sessionId: string, mediaId: string) =>
      `/ai-video-uncensored/${sessionId}/${mediaId}`,
    WITH_START_FRAME: ({
      prompt,
      startFrameUrl,
      model,
    }: {
      prompt: string
      startFrameUrl: string
      model: string
    }) =>
      `/ai-video-uncensored?${new URLSearchParams({ prompt, startFrameUrl, model }).toString()}`,
  },
  DRAFT_MODE: {
    ROOT: '/draft-mode',
    FAVORITES: '/draft-mode/favorites',
    MY_VIDEOS: '/draft-mode/my-videos',
    SESSION: (id?: string) => (id ? `/draft-mode/${id}` : '/draft-mode/:sessionId'),
    SESSION_MEDIA: (sessionId: string, mediaId: string) => `/draft-mode/${sessionId}/${mediaId}`,
  },
  AI_SOUND_EFFECT_GENERATOR: {
    ROOT: '/ai-sound-effect-generator',
    MY_SOUNDS: '/ai-sound-effect-generator/my-sounds',
    TRENDING: '/ai-sound-effect-generator/trending',
    ARTIST: (userSlug?: string) =>
      userSlug
        ? `/ai-sound-effect-generator/artist/${userSlug}`
        : '/ai-sound-effect-generator/artist/:userSlug',
    SESSION: (id?: string) =>
      id ? `/ai-sound-effect-generator/${id}` : '/ai-sound-effect-generator/:sessionId',
  },
  AI_SPEECH_GENERATOR: {
    ROOT: '/ai-speech-generator',
    SESSION: (id?: string) =>
      id ? `/ai-speech-generator/${id}` : '/ai-speech-generator/:sessionId',
  },
  AI_SPEECH_GENERATOR_V1: {
    ROOT: '/ai-speech-generator-v1',
    SESSION: (id?: string) =>
      id ? `/ai-speech-generator-v1/${id}` : '/ai-speech-generator-v1/:sessionId',
  },
  // Desktop-only local Text to Speech. Reuses the legacy Speech Generator
  // implementation, but is reachable only from the desktop "Local models"
  // navigation; web visitors are redirected to `AI_SPEECH_GENERATOR`.
  TEXT_TO_SPEECH: {
    ROOT: '/text-to-speech',
    SESSION: (id?: string) => (id ? `/text-to-speech/${id}` : '/text-to-speech/:sessionId'),
  },
  // Desktop-only local Voice workspace: Kokoro presets, local voice clones and
  // a voiceover timeline. Separate from `TEXT_TO_SPEECH`, which stays the plain
  // single-voice generator, and local-only — web visitors get the desktop
  // notice rather than a redirect to `AI_SPEECH_GENERATOR`.
  VOICE: {
    ROOT: '/voice',
  },
  // Registered on web and desktop. Next steps stay hidden until the desktop
  // image-actions flag is on, so turning the flag on does not need a rebuild.
  IMAGE_CHAT: {
    ROOT: '/image-chat',
  },
  THREED_MODEL_VIEWER: {
    ROOT: '/3d-model-viewer',
  },
  AI_PHOTO_STYLES: {
    ROOT: '/ai-photo-styles',
    STYLES: '/ai-photo-styles/styles',
    FAVORITES: '/ai-photo-styles/favorites',
    GENERATE: '/ai-photo-styles/generate',
    LIGHTBOX: (sessionId: string) => `/ai-photo-styles/generate/${sessionId}`,
  },
  // Entry point only: the tool derives its inner URLs from wherever it is mounted.
  YOUTUBE_THUMBNAIL_GENERATOR: {
    ROOT: '/youtube-thumbnail-generator',
  },
  AI_MOCKUP_GENERATOR: {
    ROOT: '/ai-mockup-generator',
    SCENES: '/ai-mockup-generator/scenes',
    FAVORITES: '/ai-mockup-generator/favorites',
    GENERATE: '/ai-mockup-generator/generate',
    LIGHTBOX: (sessionId: string) => `/ai-mockup-generator/generate/${sessionId}`,
  },
  AD_CAMPAIGN_GENERATOR: {
    ROOT: '/ad-campaign-generator',
  },
  SUBTITLE_GENERATOR: {
    ROOT: '/subtitle-generator',
  },
  THREED_PRINT_GENERATOR: {
    ROOT: '/3d-print-generator',
    FAVORITES: '/3d-print-generator/favorites',
    MY_PRINTS: '/3d-print-generator/my-prints',
    SESSION: (id?: string) => (id ? `/3d-print-generator/${id}` : '/3d-print-generator/:sessionId'),
    SESSION_BY_IMAGE: (image?: string) =>
      image
        ? `/3d-print-generator?image=${encodeURIComponent(image)}&force_submit=true`
        : '/3d-print-generator',
  },
  THREED_MODEL_GENERATOR: {
    ROOT: '/3d-model-generator',
    FAVORITES: '/3d-model-generator/favorites',
    MY_MODELS: '/3d-model-generator/my-models',
    SESSION: (id?: string) => (id ? `/3d-model-generator/${id}` : '/3d-model-generator/:sessionId'),
  },
  AI_VIDEO_UPSCALER: {
    ROOT: '/ai-video-upscaler',
    MY_VIDEOS: '/ai-video-upscaler/my-videos',
    SESSION: (id?: string) => (id ? `/ai-video-upscaler/${id}` : '/ai-video-upscaler/:sessionId'),
    FROM_DRAFT: (sessionId: string, mediaId: string) =>
      `/ai-video-upscaler?${new URLSearchParams({
        [DRAFT_UPSCALE_QUERY_PARAMS.SESSION_ID]: sessionId,
        [DRAFT_UPSCALE_QUERY_PARAMS.MEDIA_ID]: mediaId,
      }).toString()}`,
  },
  VIRTUAL_TRY_ON: {
    ROOT: '/virtual-try-on',
  },
  VIDEO_EDITING_TOOLBOX: {
    ROOT: '/video-editing-toolbox',
    TOOL: (slug?: string) =>
      slug ? `/video-editing-toolbox/${slug}` : '/video-editing-toolbox/:toolSlug',
  },
  THREED_SCULPT: {
    ROOT: '/3d-sculpt',
  },
  WORLD_CREATOR: {
    ROOT: '/world-creator',
    COMMUNITY: '/world-creator',
    FAVORITES: '/world-creator/favorites',
    MY_WORLDS: '/world-creator/my-worlds',
    SESSION: (id?: string) => (id ? `/world-creator/${id}` : '/world-creator/:sessionId'),
  },
  CAMERA_ANGLE: {
    ROOT: '/camera-angle',
    SESSION: (id?: string) => (id ? `/camera-angle/${id}` : '/camera-angle/:id'),
    SESSION_BY_IMAGE: (image?: string) =>
      image ? `/camera-angle?image=${encodeURIComponent(image)}` : '/camera-angle',
  },
  COLOR_CONVERTER: {
    ROOT: '/color-converter',
  },
  CONTRAST_CHECKER: {
    ROOT: '/contrast-checker',
  },
  LUMO: {
    ROOT: '/canvas/lumo',
  },
  CANVAS: {
    ROOT: '/canvas',
  },
  WORKFLOWS: {
    ROOT: '/workflows',
    WORKFLOW: (id?: string) => (id ? `/workflows/${id}` : '/workflows/:workflowId'),
  },
  PLATFORM: {
    ROOT: '/platform',
    MODELS: '/platform/models',
    MODEL: (model?: string) => (model ? `/platform/models/${model}` : '/platform/models/:model'),
    DOCS: '/platform/docs',
    PRICING: '/platform/pricing',
    GET_API_KEY: '/platform/get-api-key',
  },
  DESKTOP: {
    ROOT: '/desktop',
    THANK_YOU: '/desktop/thank-you',
    FREE_TTS: '/desktop/free-tts',
    FREE_IMAGE: '/desktop/free-image',
    FREE_UPSCALER: '/desktop/free-upscaler',
    FREE_BACKGROUND_REMOVER: '/desktop/free-background-remover',
  },
  MOMENTS: {
    ROOT: '/moments',
    GENERATE: '/moments',
    GENERATE_LEGACY: '/moments/generate',
    GENERATE_PROJECT: (projectId?: string) =>
      projectId ? `/moments/generate/${projectId}` : '/moments/generate/:projectId',
    MY_MOMENTS: '/moments/my-moments',
    CHAT: '/moments/chat',
    CHAT_PROJECT: (projectId?: string) =>
      projectId ? `/moments/chat/${projectId}` : '/moments/chat/:projectId',
    SHARE: (projectId?: string) =>
      projectId ? `/moments/share/${projectId}` : '/moments/share/:projectId',
  },
  AI_SVG_GENERATOR: {
    ROOT: '/ai-svg-generator',
    COMMUNITY: '/ai-svg-generator',
    MY_SVGS: '/ai-svg-generator/my-svgs',
    FAVORITES: '/ai-svg-generator/favorites',
    SESSION: (id?: string) => (id ? `/ai-svg-generator/${id}` : '/ai-svg-generator/:sessionId'),
  },
  CHARACTERS: {
    ROOT: '/characters',
    HOME: '/characters',
    YOUR_CHARACTERS: '/characters/your-characters',
    PRESETS: '/characters/presets',
    COMMUNITY: '/characters/community',
    FAVORITES: '/characters/favorites',
    LIGHTBOX: (id?: string) =>
      id ? `/characters/lightbox/${id}` : '/characters/lightbox/:assetId',
  },
  PRODUCT_MANAGER: {
    ROOT: '/product-manager',
    HOME: '/product-manager',
    YOUR_PRODUCTS: '/product-manager/your-products',
    PRESETS: '/product-manager/presets',
    COMMUNITY: '/product-manager/community',
    FAVORITES: '/product-manager/favorites',
    LIGHTBOX: (id?: string) =>
      id ? `/product-manager/lightbox/${id}` : '/product-manager/lightbox/:assetId',
  },
  IMAGE_ALIVE: {
    ROOT: '/image-alive',
  },
  CHARACTER_SHEET: {
    ROOT: '/character-sheet',
  },
  FREE_IMAGE_GENERATOR: {
    ROOT: '/free-image-generator',
  },
  FREE_VIDEO_GENERATOR: {
    ROOT: '/free-video-generator',
  },
  VIDEO_FRAME_INTERPOLATION: {
    ROOT: '/video-frame-interpolation',
  },
  LOCAL_ASSETS: {
    ROOT: '/assets',
  },
  LORA_TRAINING: {
    ROOT: '/lora-training',
  },
  MARKETING_TEMPLATES: {
    ROOT: '/marketing-templates',
  },
  MARKETING_STUDIO: {
    ROOT: '/marketing-studio',
    HOME: '/marketing-studio',
    MARKETING_TEMPLATES: '/marketing-studio/marketing-templates',
    VIDEO_AD_GENERATOR: '/marketing-studio/video-ad-generator',
    AD_CAMPAIGN_GENERATOR: '/marketing-studio/ad-campaign-generator',
    SUBTITLE_GENERATOR: '/marketing-studio/subtitle-generator',
    AD_BANNER_RESIZER: {
      ROOT: '/marketing-studio/ad-banner-resizer',
      SESSION: (sessionId: string) => `/marketing-studio/ad-banner-resizer/${sessionId}`,
    },
    YOUTUBE_THUMBNAIL_GENERATOR: '/marketing-studio/youtube-thumbnail-generator',
    BRAND_MANAGER: {
      ROOT: '/marketing-studio/brand-manager',
      BRAND_KIT: (brandKitId?: string) =>
        `/marketing-studio/brand-manager/${brandKitId ?? ':brandKitId'}`,
    },
    CHARACTER_MANAGER: assetAppRoutes('/marketing-studio/character-manager', 'your-characters'),
    PRODUCT_MANAGER: assetAppRoutes('/marketing-studio/product-manager', 'your-products'),
  },
  FREEMODE: {
    ROOT: '/freemode',
    HOME: '/freemode',
    CHARACTERS: '/freemode/characters',
    HORROR_GORE: '/freemode/horror-gore',
    MANGA_ANIME: '/freemode/manga-anime',
    SPICY_CONTENT: '/freemode/spicy-content',
    AI_COMPANIONSHIP: '/freemode/ai-companionship',
    UNFILTERED_ROLEPLAY: '/freemode/unfiltered-roleplay',
    FAN_ART: '/freemode/fan-art',
    AI_IMAGE_GENERATOR: '/freemode/ai-image-generator',
    AI_VIDEO_GENERATOR: {
      ROOT: '/freemode/ai-video-generator',
      COMMUNITY: '/freemode/ai-video-generator/community',
      FAVORITES: '/freemode/ai-video-generator/favorites',
      MY_VIDEOS: '/freemode/ai-video-generator/my-videos',
      TEMPLATES: '/freemode/ai-video-generator/templates',
      MOTION: '/freemode/ai-video-generator/motion',
      SESSION: (id?: string) =>
        id ? `/freemode/ai-video-generator/${id}` : '/freemode/ai-video-generator/:sessionId',
      SESSION_MEDIA: (sessionId: string, mediaId: string) =>
        `/freemode/ai-video-generator/${sessionId}/${mediaId}`,
    },
    AI_VIDEO_EDITOR: {
      ROOT: '/freemode/ai-video-editor',
      SESSION: (id?: string) =>
        id ? `/freemode/ai-video-editor/${id}` : '/freemode/ai-video-editor/:sessionId',
      WITH_VIDEO: (video: string) =>
        `/freemode/ai-video-editor?${new URLSearchParams({ video }).toString()}`,
      SESSION_BY_VIDEO_AND_PROMPT: (video: string, prompt: string) =>
        `/freemode/ai-video-editor?video=${encodeURIComponent(video)}&prompt=${encodeURIComponent(prompt)}`,
    },
    AI_IMAGE_EDITOR: {
      ROOT: '/freemode/ai-image-editor',
      SESSION: (id?: string) =>
        id ? `/freemode/ai-image-editor/${id}` : '/freemode/ai-image-editor/:sessionId',
      SESSION_BY_IMAGE: (image?: string) =>
        image
          ? `/freemode/ai-image-editor?image=${encodeURIComponent(image)}`
          : '/freemode/ai-image-editor',
      SESSION_BY_IMAGE_AND_PROMPT: (image: string, prompt: string) =>
        `/freemode/ai-image-editor?image=${encodeURIComponent(image)}&prompt=${encodeURIComponent(prompt)}`,
    },
    CHAT: {
      ROOT: '/freemode/chat',
      MODEL: (modelId?: string) =>
        modelId ? `/freemode/chat/${modelId}` : '/freemode/chat/:modelId',
    },
    AI_VIDEO_UNCENSORED: {
      ROOT: '/freemode/ai-video-uncensored',
      MY_VIDEOS: '/freemode/ai-video-uncensored/my-videos',
      TEMPLATES: '/freemode/ai-video-uncensored/templates',
      SESSION: (id?: string) =>
        id ? `/freemode/ai-video-uncensored/${id}` : '/freemode/ai-video-uncensored/:sessionId',
      SESSION_MEDIA: (sessionId: string, mediaId: string) =>
        `/freemode/ai-video-uncensored/${sessionId}/${mediaId}`,
      WITH_START_FRAME: ({
        prompt,
        startFrameUrl,
        model,
      }: {
        prompt: string
        startFrameUrl: string
        model: string
      }) =>
        `/freemode/ai-video-uncensored?${new URLSearchParams({ prompt, startFrameUrl, model }).toString()}`,
    },
  },
  MODELS: {
    ROOT: '/models',
  },
  UNLIMITED: {
    ROOT: '/unlimited',
    HOME: '/unlimited',
    IMAGE_GENERATOR: '/unlimited/image-generator',
    BACKGROUND_REMOVER: '/unlimited/background-remover',
    UPSCALER: '/unlimited/image-upscaler',
    VECTORIZER: '/unlimited/vectorizer',
    SPEECH_GENERATOR: '/unlimited/speech-generator',
    VIDEO_GENERATOR: {
      ROOT: '/unlimited/video-generator',
      MODELS: '/unlimited/video-generator/models',
      FAVORITES: '/unlimited/video-generator/favorites',
      MY_VIDEOS: '/unlimited/video-generator/my-videos',
      SESSION: (id?: string) =>
        id ? `/unlimited/video-generator/${id}` : '/unlimited/video-generator/:sessionId',
      SESSION_MEDIA: (sessionId: string, mediaId: string) =>
        `/unlimited/video-generator/${sessionId}/${mediaId}`,
    },
  },
  THREED_WORKSPACE: {
    ROOT: '/3d',
    HOME: '/3d',
  },
  CATEGORY: {
    IMAGE: '/image',
    VIDEO: '/video',
    AUDIO: '/audio',
    THREE_D: '/3d-tools',
    MARKETING: '/marketing',
    UTILITIES: '/utilities',
    ENTERTAINMENT: '/entertainment',
    CRAFTING: '/crafting',
  },
}

/** Staging/preview/localhost surfaces (`/_components`, `/_aida`, …). Not a product path — not localized. */
export function isStudioInternalGalleryPathname(pathname: string): boolean {
  const unprefixed = stripStudioLocalePrefixFromPathname(pathname)
  const firstSegment = unprefixed.split('/').find(segment => segment.length > 0)
  return firstSegment?.startsWith('_') === true
}

export function isLocalePrefixedStudioInternalGalleryPathname(pathname: string): boolean {
  return (
    getStudioLanguageFromPathname(pathname) !== undefined &&
    isStudioInternalGalleryPathname(pathname)
  )
}
